import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  query,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { COLLECTIONS } from '../../constants/firebaseConfig';
import { CART_STATUS, StatusHelpers } from '../../constants';
import { PUBLIC_PRODUCTS_SUBSCRIPTION_PURPOSE } from '../publicProductsSubscriptionPayment';
import { parseWipayDataQueryParam } from './parseWipayCallback';
import { findCartInstanceInCartData } from './resolveCartInstance';
import { createLogger } from '../../utils/logger';

const logger = createLogger('listUnprocessedPayments');

const SUBSCRIPTION_PAYMENT_KIND = 'public_products_subscription';

const FULFILLED_TRANSACTION_STATUSES = [
  CART_STATUS.AWAITING_PACKING,
  CART_STATUS.PACKED_AWAITING_PICKUP,
  CART_STATUS.COMPLETED,
  'payment_successful',
];

export const PERSISTENCE_ISSUE = {
  WIPAY_SUCCESS_NO_TRANSACTION: 'wipay_success_no_transaction',
  WIPAY_SUCCESS_CART_NOT_UPDATED: 'wipay_success_cart_not_updated',
  WIPAY_SUCCESS_INVENTORY_INCOMPLETE: 'wipay_success_inventory_incomplete',
  WIPAY_SUCCESS_BAD_TRANSACTION: 'wipay_success_bad_transaction',
  WIPAY_UNFINALIZED: 'wipay_unfinalized',
  STUCK_CHECKOUT: 'stuck_checkout',
};

const ISSUE_LABELS = {
  [PERSISTENCE_ISSUE.WIPAY_SUCCESS_NO_TRANSACTION]:
    'WiPay succeeded — no Firestore transaction',
  [PERSISTENCE_ISSUE.WIPAY_SUCCESS_CART_NOT_UPDATED]:
    'WiPay succeeded — cart not moved to awaiting packing',
  [PERSISTENCE_ISSUE.WIPAY_SUCCESS_INVENTORY_INCOMPLETE]:
    'WiPay succeeded — inventory not decremented',
  [PERSISTENCE_ISSUE.WIPAY_SUCCESS_BAD_TRANSACTION]:
    'WiPay succeeded — transaction has invalid status',
  [PERSISTENCE_ISSUE.WIPAY_UNFINALIZED]: 'WiPay callback not fully finalized',
  [PERSISTENCE_ISSUE.STUCK_CHECKOUT]: 'Checkout started — no WiPay callback',
};

function toDate(value) {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (value instanceof Date) return value;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function normalizeWipayStatus(status) {
  return String(status || '').toLowerCase().trim();
}

function isWipayPaymentSuccessful(wipayData) {
  return wipayData != null && normalizeWipayStatus(wipayData.status) === 'success';
}

function isAdminResolved(wipayData, transactionSnap) {
  if (wipayData?.admin_resolved) return true;
  if (transactionSnap?.exists()) {
    const tx = transactionSnap.data();
    if (tx.admin_resolved || tx.adminResolved) return true;
  }
  return false;
}

/**
 * Detect why a payment still needs admin attention, with emphasis on WiPay success
 * that did not fully persist to Firestore (transaction, cart, inventory).
 * @returns {string|null} issue type key or null if fully persisted
 */
export function detectPersistenceIssue({ wipayData, transactionSnap, cartMatch }) {
  if (isAdminResolved(wipayData, transactionSnap)) {
    return null;
  }

  const wipaySucceeded = isWipayPaymentSuccessful(wipayData);
  const hasTransaction = Boolean(transactionSnap?.exists());
  const cartStatus = cartMatch?.cartInstance?.status;
  const tx = hasTransaction ? transactionSnap.data() : null;
  const txStatus = tx?.status;

  if (!wipayData && cartMatch?.cartInstance?.orderid) {
    return PERSISTENCE_ISSUE.STUCK_CHECKOUT;
  }

  if (wipaySucceeded && !hasTransaction) {
    return PERSISTENCE_ISSUE.WIPAY_SUCCESS_NO_TRANSACTION;
  }

  if (wipaySucceeded && hasTransaction) {
    if (
      !txStatus ||
      txStatus === 'initiated' ||
      txStatus === CART_STATUS.PAYMENT_FAILED ||
      !FULFILLED_TRANSACTION_STATUSES.includes(txStatus)
    ) {
      return PERSISTENCE_ISSUE.WIPAY_SUCCESS_BAD_TRANSACTION;
    }

    if (
      cartStatus === CART_STATUS.ACTIVE ||
      (cartStatus && StatusHelpers.canUpdateToAwaitingPacking(cartStatus))
    ) {
      return PERSISTENCE_ISSUE.WIPAY_SUCCESS_CART_NOT_UPDATED;
    }

    const cartItems = tx.cartItems;
    if (
      txStatus === CART_STATUS.AWAITING_PACKING &&
      Array.isArray(cartItems) &&
      cartItems.length > 0 &&
      tx.inventoryDecremented !== true
    ) {
      return PERSISTENCE_ISSUE.WIPAY_SUCCESS_INVENTORY_INCOMPLETE;
    }

    return null;
  }

  if (wipayData && !wipaySucceeded) {
    if (!hasTransaction) {
      return PERSISTENCE_ISSUE.WIPAY_UNFINALIZED;
    }
    if (txStatus !== CART_STATUS.PAYMENT_FAILED) {
      return PERSISTENCE_ISSUE.WIPAY_UNFINALIZED;
    }
    return null;
  }

  if (!wipayData && hasTransaction && txStatus === CART_STATUS.PAYMENT_FAILED) {
    return null;
  }

  return PERSISTENCE_ISSUE.WIPAY_UNFINALIZED;
}

async function resolveCustomerDetails(userId, fallbackName = 'Unknown Customer') {
  if (!userId || userId === 'unknown') {
    return { name: fallbackName, email: null };
  }
  try {
    const userQuery = query(
      collection(db, COLLECTIONS.USERS),
      where('identity_id', '==', userId),
      limit(1)
    );
    const userSnapshot = await getDocs(userQuery);
    if (!userSnapshot.empty) {
      const userData = userSnapshot.docs[0].data();
      return {
        name: userData.first_name || userData.displayName || fallbackName,
        email: userData.email || null,
      };
    }
  } catch (error) {
    logger.warn('[listUnprocessedPayments] Failed to resolve customer details:', error);
  }
  return { name: fallbackName, email: null };
}

async function resolveCustomerName(userId, fallback = 'Unknown Customer') {
  const details = await resolveCustomerDetails(userId, fallback);
  return details.name;
}

function buildRowBase({
  id,
  orderId,
  source,
  wipayData,
  checkoutData,
  transactionSnap,
  cartMatch,
  issueType,
}) {
  const data = wipayData || {};
  const userId =
    checkoutData.userId ||
    checkoutData.user_id ||
    data.user_id ||
    cartMatch?.cartInstance?.userId ||
    null;

  const customerName =
    checkoutData.customerName ||
    cartMatch?.cartInstance?.customerName ||
    data.customer_name ||
    null;

  const supermarketName =
    checkoutData.supermarketName ||
    cartMatch?.cartInstance?.supermarketName ||
    data.supermarket_name ||
    'Unknown Supermarket';

  const wipaySucceeded = isWipayPaymentSuccessful(wipayData);

  return {
    id,
    orderId,
    source,
    issueType,
    issueLabel: ISSUE_LABELS[issueType] || issueType,
    wipayStatus: wipayData ? data.status || 'unknown' : 'no_callback',
    wipaySucceeded,
    customerName,
    customerEmail: null,
    customerUserId: userId,
    supermarketName,
    supermarketId:
      checkoutData.supermarketId ||
      checkoutData.supermarket_id ||
      cartMatch?.supermarketId ||
      data.supermarket_id ||
      null,
    total: parseFloat(data.total) || parseFloat(checkoutData.orderSubtotal) || 0,
    currency: data.currency || checkoutData.currency || 'JMD',
    wipayTransactionId: data.transaction_id || null,
    paymentMethod: data.method || null,
    message: data.message || null,
    receivedAt: toDate(data.received_at),
    hasTransaction: Boolean(transactionSnap?.exists()),
    transactionStatus: transactionSnap?.exists() ? transactionSnap.data().status : null,
    cartStatus: cartMatch?.cartInstance?.status || null,
    cartInstanceId:
      checkoutData.cartInstanceId ||
      cartMatch?.cartInstanceId ||
      orderId,
    paymentKind: data.payment_kind || null,
    adminResolved: Boolean(data.admin_resolved),
    firestorePersisted: issueType == null,
  };
}

function isSubscriptionPayment(data, checkoutData) {
  if (data?.payment_kind === SUBSCRIPTION_PAYMENT_KIND) return true;
  return checkoutData?.payment_purpose === PUBLIC_PRODUCTS_SUBSCRIPTION_PURPOSE;
}

export function buildCartDocsByOrderId(cartsSnapshot) {
  const cartDocsByOrderId = new Map();
  for (const cartDoc of cartsSnapshot.docs) {
    const cartData = cartDoc.data();
    for (const supermarketId in cartData) {
      if (supermarketId === 'lastGlobalUpdate') continue;
      const supermarketCart = cartData[supermarketId];
      if (!supermarketCart || typeof supermarketCart !== 'object') continue;

      for (const cartInstanceId of Object.keys(supermarketCart)) {
        const cartInstance = supermarketCart[cartInstanceId];
        if (!cartInstance?.orderid) continue;
        cartDocsByOrderId.set(cartInstance.orderid, {
          cartDoc,
          cartMatch: {
            supermarketId,
            supermarketCart,
            cartInstanceId,
            cartInstance,
          },
        });
      }
    }
  }
  return cartDocsByOrderId;
}

async function enrichPaymentRow(row) {
  const details = await resolveCustomerDetails(row.customerUserId, row.customerName || 'Unknown Customer');
  row.customerName = details.name;
  row.customerEmail = details.email;
  return row;
}

export async function buildPaymentRowForOrderId(orderId, cartDocsByOrderId = null) {
  if (!orderId) return null;

  let cartIndex = cartDocsByOrderId;
  if (!cartIndex) {
    const cartsSnapshot = await getDocs(collection(db, COLLECTIONS.CARTS));
    cartIndex = buildCartDocsByOrderId(cartsSnapshot);
  }

  const wipaySnap = await getDoc(doc(db, COLLECTIONS.WIPAY_PAYMENTS, orderId));
  const wipayData = wipaySnap.exists() ? wipaySnap.data() : null;
  const checkoutData = parseWipayDataQueryParam(wipayData?.data);
  if (isSubscriptionPayment(wipayData || {}, checkoutData)) return null;

  const transactionSnap = await getDoc(doc(db, COLLECTIONS.TRANSACTIONS, orderId));
  const cartMatchInfo = cartIndex.get(orderId);

  if (!wipayData && !transactionSnap.exists() && !cartMatchInfo) {
    return null;
  }

  const issueType = detectPersistenceIssue({
    wipayData,
    transactionSnap,
    cartMatch: cartMatchInfo?.cartMatch,
  });

  const source = wipayData
    ? 'wipay_payment'
    : cartMatchInfo
      ? 'stuck_cart'
      : 'transaction_only';

  const row = buildRowBase({
    id: wipaySnap.exists() ? wipaySnap.id : `search_${orderId}`,
    orderId,
    source,
    wipayData: wipayData || {},
    checkoutData:
      cartMatchInfo?.cartMatch?.cartInstance?.checkoutSnapshot || checkoutData || {},
    transactionSnap,
    cartMatch: cartMatchInfo?.cartMatch,
    issueType,
  });

  if (transactionSnap.exists()) {
    const tx = transactionSnap.data();
    row.wipayTransactionId =
      row.wipayTransactionId || tx.paymentGatewayTransactionId || null;
    row.customerName = row.customerName || tx.customerName || null;
    row.customerUserId = row.customerUserId || tx.userId || null;
    row.cartInstanceId = row.cartInstanceId || tx.cartInstanceId || orderId;
    row.cartDocId = row.cartDocId || tx.cartDocId || null;
    row.supermarketName = row.supermarketName || tx.supermarketName || row.supermarketName;
    row.total = row.total || tx.total || tx.amountPaid || 0;
  }

  if (cartMatchInfo?.cartDoc) {
    row.cartDocId = row.cartDocId || cartMatchInfo.cartDoc.id;
  }

  if (!issueType) {
    row.issueLabel = 'Fully persisted';
    row.firestorePersisted = true;
  }

  if (!wipaySnap.exists() && cartMatchInfo) {
    row.message =
      row.message || 'Checkout started; no WiPay callback recorded in Firestore';
  }

  return enrichPaymentRow(row);
}

/**
 * Load cart payments that still need admin resolution.
 * @returns {Promise<Array<object>>}
 */
export async function listUnprocessedPayments() {
  const pendingRows = [];
  const seenOrderIds = new Set();

  const wipaySnapshot = await getDocs(collection(db, COLLECTIONS.WIPAY_PAYMENTS));
  const cartsSnapshot = await getDocs(collection(db, COLLECTIONS.CARTS));

  const cartDocsByOrderId = buildCartDocsByOrderId(cartsSnapshot);

  for (const wipayDoc of wipaySnapshot.docs) {
    const data = wipayDoc.data();
    const checkoutData = parseWipayDataQueryParam(data.data);
    if (isSubscriptionPayment(data, checkoutData)) continue;

    const orderId = data.order_id || wipayDoc.id;
    seenOrderIds.add(orderId);

    const transactionSnap = await getDoc(doc(db, COLLECTIONS.TRANSACTIONS, orderId));
    const cartMatchInfo = cartDocsByOrderId.get(orderId);

    const issueType = detectPersistenceIssue({
      wipayData: data,
      transactionSnap,
      cartMatch: cartMatchInfo?.cartMatch,
    });

    if (!issueType) continue;

    const row = buildRowBase({
      id: wipayDoc.id,
      orderId,
      source: 'wipay_payment',
      wipayData: data,
      checkoutData,
      transactionSnap,
      cartMatch: cartMatchInfo?.cartMatch,
      issueType,
    });

    if (cartMatchInfo?.cartDoc) {
      row.cartDocId = cartMatchInfo.cartDoc.id;
    }

    await enrichPaymentRow(row);
    pendingRows.push(row);
  }

  for (const [orderId, { cartDoc, cartMatch }] of cartDocsByOrderId.entries()) {
    if (seenOrderIds.has(orderId)) continue;

    const wipaySnap = await getDoc(doc(db, COLLECTIONS.WIPAY_PAYMENTS, orderId));
    const wipayData = wipaySnap.exists() ? wipaySnap.data() : null;
    const checkoutData = parseWipayDataQueryParam(wipayData?.data);
    if (isSubscriptionPayment(wipayData || {}, checkoutData)) continue;

    const transactionSnap = await getDoc(doc(db, COLLECTIONS.TRANSACTIONS, orderId));

    const issueType = detectPersistenceIssue({
      wipayData,
      transactionSnap,
      cartMatch,
    });

    if (!issueType) continue;

    const row = buildRowBase({
      id: `cart_${cartDoc.id}_${cartMatch.cartInstanceId}`,
      orderId,
      source: wipaySnap.exists() ? 'wipay_payment' : 'stuck_cart',
      wipayData: wipayData || {},
      checkoutData: cartMatch.cartInstance.checkoutSnapshot || checkoutData,
      transactionSnap,
      cartMatch,
      issueType,
    });
    row.cartDocId = cartDoc.id;

    if (!wipaySnap.exists()) {
      row.message =
        row.message || 'Checkout started; no WiPay callback recorded in Firestore';
    }

    await enrichPaymentRow(row);
    pendingRows.push(row);
  }

  pendingRows.sort((a, b) => {
    if (a.wipaySucceeded !== b.wipaySucceeded) {
      return a.wipaySucceeded ? -1 : 1;
    }
    const aTime = a.receivedAt?.getTime() || 0;
    const bTime = b.receivedAt?.getTime() || 0;
    return bTime - aTime;
  });

  return pendingRows;
}

/**
 * Enrich a pending payment row with a resolved customer display name.
 * @param {object} row
 */
export async function enrichPaymentRowCustomerName(row) {
  if (row.customerName && row.customerName !== 'Unknown Customer') {
    return row;
  }
  return {
    ...row,
    customerName: await resolveCustomerName(row.customerUserId, row.customerName || 'Unknown Customer'),
  };
}

export { resolveCustomerName, resolveCustomerDetails, findCartInstanceInCartData };
